/* assets/cbi-copy.js
 * Click an email address to copy it, after the pattern on buttonwoodandco.com
 * (Kurt).
 *
 * Every plain mailto link gets a clipboard mark, hidden until the link is
 * hovered or focused, and a click copies the address instead of opening a mail
 * client. For a second afterwards the link reads Copied!, the mark becomes a
 * check and holds on its own, which is the only way a tap ever sees the
 * confirmation. Buttonwood's own timing and wording, from copy-email.js on
 * that theme.
 *
 * It does not matter whether the link shows the address or a label: Press
 * Inquiry in the footer copies pr@vanderwolkco.com (Kurt). A mailto carrying a
 * subject or a body is the exception and is left alone, because the composed
 * message is the point of it. Inquire on a sold product is the one we have.
 *
 * The other exception is an address written into a sentence. Holding room for
 * the mark leaves a hole mid line, so anything under data-cbi-copy-off keeps
 * its plain link: the cart's delivery note is the one we have.
 *
 * Progressive enhancement throughout. Without JavaScript the link is an
 * ordinary mailto. A modified click, a middle click or a context menu is left
 * to the browser, so open in new tab and copy link address still work. If the
 * clipboard refuses, the click falls through to the mail client rather than
 * doing nothing.
 */

(() => {
  const HOLD = 1000; /* Buttonwood holds the confirmation for a second. */
  const COPIED = 'Copied!';
  const template = document.getElementById('cbi-copy-icons');
  if (!template) return;

  const timers = new WeakMap();

  /* The address out of the href, or nothing if this is not a link to copy. A
   * mailto with a query has a subject or a body on it, so someone clicking it
   * wants the composed message and should get their mail client. */
  const addressOf = (link) => {
    const href = link.getAttribute('href') || '';
    if (!/^mailto:/i.test(href)) return null;
    if (href.includes('?')) return null;
    let address = href.slice(7);
    try {
      address = decodeURIComponent(address);
    } catch (error) {
      /* A stray percent in the href. Take it as written. */
    }
    address = address.trim();
    return address || null;
  };

  const status = (() => {
    let region = null;
    return (message) => {
      if (!region) {
        region = document.createElement('p');
        region.className = 'cbi-visually-hidden';
        region.setAttribute('role', 'status');
        document.body.append(region);
      }
      /* Cleared first, so copying the same address twice is announced twice. */
      region.textContent = '';
      window.setTimeout(() => {
        region.textContent = message;
      }, 50);
    };
  })();

  const write = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (error) {
        /* Denied permission or an insecure frame. Try the old way. */
      }
    }
    const field = document.createElement('textarea');
    field.value = text;
    field.setAttribute('readonly', '');
    field.style.cssText = 'position:fixed;top:0;left:-9999px;opacity:0;';
    document.body.append(field);
    field.select();
    let done = false;
    try {
      done = document.execCommand('copy');
    } catch (error) {
      done = false;
    }
    field.remove();
    return done;
  };

  const confirm = (link) => {
    window.clearTimeout(timers.get(link));
    link.classList.add('is-copied');
    timers.set(
      link,
      window.setTimeout(() => link.classList.remove('is-copied'), HOLD)
    );
  };

  const enhance = (link) => {
    if (link.dataset.cbiCopy) return;
    const address = addressOf(link);
    if (!address) return;
    link.dataset.cbiCopy = address;
    link.classList.add('cbi-copy');

    /* Both wordings are in the page at once, one over the other in a single
     * grid cell, and only one of them is visible. The link is then as wide as
     * the longer of the two whichever is showing, so nothing beside or below
     * it moves when the confirmation appears (Kurt). Visibility, not display,
     * so the hidden one keeps its size and stays out of the accessibility
     * tree. */
    const label = document.createElement('span');
    label.className = 'cbi-copy__label';
    const shown = document.createElement('span');
    shown.className = 'cbi-copy__text cbi-copy__text--rest';
    shown.textContent = link.textContent.trim();
    const copied = document.createElement('span');
    copied.className = 'cbi-copy__text cbi-copy__text--copied';
    copied.textContent = COPIED;
    label.append(shown, copied);

    /* The marks only, not the newlines between them in the template: inside an
     * inline link those would print as spaces on top of the mark's own. */
    const marks = template.content.cloneNode(true);
    for (const node of [...marks.childNodes]) {
      if (node.nodeType === Node.TEXT_NODE) node.remove();
    }
    link.textContent = '';
    link.append(label, marks);
  };

  /* A link standing on its own can hold room for the mark and the Copied!
   * label without anything moving. One inside a sentence cannot: the room it
   * holds opens a gap in the prose. Those opt out with data-cbi-copy-off on
   * the link or on anything around it. */
  const enhanceAll = (root) => {
    for (const link of (root || document).querySelectorAll('a[href^="mailto:" i]')) {
      if (link.closest('[data-cbi-copy-off]')) continue;
      enhance(link);
    }
  };

  document.addEventListener('click', (event) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    const link = event.target.closest('a[data-cbi-copy]');
    if (!link) return;
    const address = link.dataset.cbiCopy;
    event.preventDefault();
    write(address).then((done) => {
      if (done) {
        confirm(link);
        status(address + ' copied');
      } else {
        /* Nothing on the clipboard, so give them the mail client instead. */
        window.location.href = link.href;
      }
    });
  });

  enhanceAll();
  document.addEventListener('shopify:section:load', (event) => enhanceAll(event.target));
})();
